// Remembers which category tab an operator last looked at, per event.
//
// Both the public results board and the venue display used to reset to the
// first category on every load — the results board on each refresh, and the
// venue display every few seconds as it rotated. On a nine-category event that
// means the tab you actually want is almost never the one on screen.
//
// The choice is stored per event id so switching between events does not carry
// a category that does not exist in the other one.

const keyFor = (eventId) => `vionix_last_category_${eventId || "unknown"}`;

/**
 * The category id last chosen for this event, or null when there is no usable
 * stored value. Storage can throw (private browsing, disabled cookies), so
 * every access is guarded — a missing preference is never worth breaking the
 * page over.
 */
export const readLastCategory = (eventId) => {
  if (!eventId) return null;
  try {
    const stored = window.localStorage.getItem(keyFor(eventId));
    return stored || null;
  } catch {
    return null;
  }
};

/** Persist the chosen category for this event. Failures are ignored. */
export const writeLastCategory = (eventId, categoryId) => {
  if (!eventId || !categoryId) return;
  try {
    window.localStorage.setItem(keyFor(eventId), categoryId);
  } catch {
    // Storage unavailable: the tab simply will not be remembered.
  }
};

/**
 * Resolve which category should open, given the ones this event actually has.
 *
 * A stored id is only honoured when it still exists in `categories` — a roster
 * edit can remove a category, and restoring a tab that is gone would leave the
 * page showing nothing. Falls back to the first category.
 */
export const resolveInitialCategory = (eventId, categories = []) => {
  if (!categories.length) return "";
  const stored = readLastCategory(eventId);
  const isStillValid = stored && categories.some((category) => category.id === stored);
  return isStillValid ? stored : categories[0].id;
};
